import React, { FC, useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { Button, Table, Form, Modal, Message, Icon, Confirm } from "semantic-ui-react";
import * as dasha from "@dasha.ai/sdk/web";
import { useStore } from "../core/api/GlobalStoreContext";
import "./styles.css";
import { ScrollbarStyle } from "../uikit";

interface CreateGroupFormData {
  name: string;
  description: string;
  maxConcurrency: number | null;
}

export const GroupsManagement: FC<{}> = observer(() => {
  const { groups } = useStore();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<{ name: string; info: dasha.groups.GroupInfo } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [createFormData, setCreateFormData] = useState<CreateGroupFormData>({
    name: "",
    description: "",
    maxConcurrency: null
  });

  const [editFormData, setEditFormData] = useState<Omit<CreateGroupFormData, 'name'>>({
    description: "",
    maxConcurrency: null
  });

  useEffect(() => {
    groups.TryReload();
  }, []);

  const handleCreateGroup = useCallback(async () => {
    if (!createFormData.name.trim()) {
      setError("Group name is required");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await groups.CreateGroup(createFormData.name, {
        description: createFormData.description,
        maxConcurrency: createFormData.maxConcurrency
      });
      setSuccess(`Group "${createFormData.name}" created successfully`);
      setIsCreateModalOpen(false);
      setCreateFormData({ name: "", description: "", maxConcurrency: null });
    } catch (err: any) {
      setError(err.message || "Failed to create group");
    } finally {
      setLoading(false);
    }
  }, [createFormData, groups]);

  const handleUpdateGroup = useCallback(async () => {
    if (!selectedGroup) return;

    setLoading(true);
    setError(null);
    try {
      await groups.UpdateGroup(selectedGroup.name, {
        description: editFormData.description,
        maxConcurrency: editFormData.maxConcurrency
      });
      setSuccess(`Group "${selectedGroup.name}" updated successfully`);
      setIsEditModalOpen(false);
      setSelectedGroup(null);
    } catch (err: any) {
      setError(err.message || "Failed to update group");
    } finally {
      setLoading(false);
    }
  }, [selectedGroup, editFormData, groups]);

  const handleDeleteGroup = useCallback(async () => {
    if (!selectedGroup) return;

    setLoading(true);
    setError(null);
    try {
      await groups.DeleteGroup(selectedGroup.name, { quiet: false });
      setSuccess(`Group "${selectedGroup.name}" deleted successfully`);
      setIsDeleteConfirmOpen(false);
      setSelectedGroup(null);
    } catch (err: any) {
      setError(err.message || "Failed to delete group");
    } finally {
      setLoading(false);
    }
  }, [selectedGroup, groups]);

  const openEditModal = (groupName: string, groupInfo: dasha.groups.GroupInfo) => {
    if (groupName === "Default") {
      setError("The Default group cannot be edited");
      return;
    }
    setSelectedGroup({ name: groupName, info: groupInfo });
    setEditFormData({
      description: groupInfo.description || "",
      maxConcurrency: groupInfo.maxConcurrency || null
    });
    setIsEditModalOpen(true);
  };

  const openDeleteConfirm = (groupName: string, groupInfo: dasha.groups.GroupInfo) => {
    if (groupName === "Default") {
      setError("The Default group cannot be deleted");
      return;
    }
    setSelectedGroup({ name: groupName, info: groupInfo });
    setIsDeleteConfirmOpen(true);
  };

  const dismissMessages = () => {
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="defaultRoot" id="grid-workspace">
        <ScrollbarStyle/>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>Groups Management</h2>
        <Button 
          primary 
          icon="plus" 
          content="Create Group" 
          onClick={() => setIsCreateModalOpen(true)}
          disabled={loading}
        />
      </div>

      {error && (
        <Message error onDismiss={dismissMessages}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {success && (
        <Message success onDismiss={dismissMessages}>
          <Message.Header>Success</Message.Header>
          <p>{success}</p>
        </Message>
      )}

      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Group Name</Table.HeaderCell>
            <Table.HeaderCell>Group ID</Table.HeaderCell>
            <Table.HeaderCell>Description</Table.HeaderCell>
            <Table.HeaderCell>Max Concurrency</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {Array.from(groups.Groups.entries()).map(([groupName, groupInfo]) => (
            <Table.Row key={groupName}>
              <Table.Cell>{groupName}</Table.Cell>
              <Table.Cell>{groupInfo.id}</Table.Cell>
              <Table.Cell>{groupInfo.description || "-"}</Table.Cell>
              <Table.Cell>{groupInfo.maxConcurrency == null ? "Unlimited" : groupInfo.maxConcurrency}</Table.Cell>
              <Table.Cell>
                <Button.Group size="small">
                  <Button 
                    icon="edit" 
                    content="Edit" 
                    onClick={() => openEditModal(groupName, groupInfo)}
                    disabled={loading || groupName === "Default"}
                    title={groupName === "Default" ? "Default group cannot be edited" : "Edit group"}
                  />
                  <Button 
                    icon="trash" 
                    content="Delete" 
                    color="red"
                    onClick={() => openDeleteConfirm(groupName, groupInfo)}
                    disabled={loading || groupName === "Default"}
                    title={groupName === "Default" ? "Default group cannot be deleted" : "Delete group"}
                  />
                </Button.Group>
              </Table.Cell>
            </Table.Row>
          ))}
          {groups.Groups.size === 0 && !groups.Loading && (
            <Table.Row>
              <Table.Cell colSpan="5" textAlign="center">
                <em>No groups found</em>
              </Table.Cell>
            </Table.Row>
          )}
          {groups.Loading && (
            <Table.Row>
              <Table.Cell colSpan="5" textAlign="center">
                <Icon loading name="spinner" /> Loading groups...
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>

      {/* Create Group Modal */}
      <Modal open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} size="small">
        <Modal.Header>Create New Group</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field required>
              <label>Group Name</label>
              <Form.Input
                placeholder="Enter group name"
                value={createFormData.name}
                onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
              />
            </Form.Field>
            <Form.Field>
              <label>Description</label>
              <Form.TextArea
                placeholder="Enter group description (optional)"
                value={createFormData.description}
                onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
              />
            </Form.Field>
            <Form.Field>
              <label>Max Concurrency</label>
              <Form.Checkbox
                label="Unlimited"
                checked={createFormData.maxConcurrency === null}
                onChange={(e, { checked }) => setCreateFormData({ 
                  ...createFormData, 
                  maxConcurrency: checked ? null : 10 
                })}
              />
              {createFormData.maxConcurrency !== null && (
                <Form.Input
                  type="number"
                  min="1"
                  placeholder="10"
                  value={createFormData.maxConcurrency || ''}
                  onChange={(e) => setCreateFormData({ 
                    ...createFormData, 
                    maxConcurrency: parseInt(e.target.value) || 10 
                  })}
                  style={{ marginTop: '0.5rem' }}
                />
              )}
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setIsCreateModalOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button primary onClick={handleCreateGroup} loading={loading}>
            Create Group
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Edit Group Modal */}
      <Modal open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="small">
        <Modal.Header>Edit Group: {selectedGroup?.name}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>Description</label>
              <Form.TextArea
                placeholder="Enter group description (optional)"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              />
            </Form.Field>
            <Form.Field>
              <label>Max Concurrency</label>
              <Form.Checkbox
                label="Unlimited"
                checked={editFormData.maxConcurrency === null}
                onChange={(e, { checked }) => setEditFormData({ 
                  ...editFormData, 
                  maxConcurrency: checked ? null : 10 
                })}
              />
              {editFormData.maxConcurrency !== null && (
                <Form.Input
                  type="number"
                  min="1"
                  placeholder="10"
                  value={editFormData.maxConcurrency || ''}
                  onChange={(e) => setEditFormData({ 
                    ...editFormData, 
                    maxConcurrency: parseInt(e.target.value) || 10 
                  })}
                  style={{ marginTop: '0.5rem' }}
                />
              )}
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setIsEditModalOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button primary onClick={handleUpdateGroup} loading={loading}>
            Update Group
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Confirm
        open={isDeleteConfirmOpen}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteGroup}
        header="Delete Group"
        content={`Are you sure you want to delete the group "${selectedGroup?.name}"? This action cannot be undone.`}
        confirmButton="Delete"
        cancelButton="Cancel"
      />
    </div>
  );
}); 